import { Node } from "@tiptap/core";
import { DOMOutputSpec } from "@tiptap/pm/model";

// Docs content blocks written in the dashboard's article editor (steps, tabs,
// cards). The widget only renders articles, so these nodes just need a schema
// and HTML output; the help center article view styles them and switches tabs.

const titleAttribute = {
  title: {
    default: "",
    parseHTML: (element: HTMLElement) => element.getAttribute("data-title") ?? "",
    renderHTML: () => ({}),
  },
};

// Cards link anywhere the author wants, so keep only schemes that can't run
// script: web links, mail, Gleap deep links and relative paths.
const safeHref = (href: unknown): string | null => {
  if (typeof href !== "string") return null;
  const value = href.trim();
  if (!value) return null;
  if (/^(https?:|mailto:|gleap:)/i.test(value)) return value;
  if (/^[/#?]/.test(value) && !value.startsWith("//")) return value;
  return null;
};

export const Steps = Node.create({
  name: "steps",
  group: "block",
  content: "step+",

  parseHTML() {
    return [{ tag: 'div[data-type="steps"]' }];
  },

  renderHTML(): DOMOutputSpec {
    return ["div", { class: "docs-steps", "data-type": "steps" }, 0];
  },
});

export const Step = Node.create({
  name: "step",
  content: "block+",
  defining: true,

  addAttributes() {
    return titleAttribute;
  },

  parseHTML() {
    return [{ tag: 'div[data-type="step"]', contentElement: ".docs-step-content" }];
  },

  renderHTML({ node }): DOMOutputSpec {
    return [
      "div",
      { class: "docs-step", "data-type": "step", "data-title": node.attrs.title },
      ["div", { class: "docs-step-title" }, node.attrs.title ?? ""],
      ["div", { class: "docs-step-content" }, 0],
    ];
  },
});

export const Tabs = Node.create({
  name: "tabs",
  group: "block",
  content: "tab+",

  parseHTML() {
    return [{ tag: 'div[data-type="tabs"]', contentElement: ".docs-tabs-panels" }];
  },

  renderHTML({ node }): DOMOutputSpec {
    const buttons: DOMOutputSpec[] = [];
    node.forEach((tab, _offset, index) => {
      buttons.push([
        "button",
        {
          type: "button",
          role: "tab",
          class: index === 0 ? "docs-tabs-button is-active" : "docs-tabs-button",
          "aria-selected": index === 0 ? "true" : "false",
          "data-index": `${index}`,
        },
        tab.attrs.title || `${index + 1}`,
      ]);
    });

    return [
      "div",
      { class: "docs-tabs", "data-type": "tabs" },
      ["div", { class: "docs-tabs-bar", role: "tablist" }, ...buttons],
      ["div", { class: "docs-tabs-panels" }, 0],
    ];
  },
});

export const Tab = Node.create({
  name: "tab",
  content: "block+",
  defining: true,

  addAttributes() {
    return titleAttribute;
  },

  parseHTML() {
    return [{ tag: 'div[data-type="tab"]' }];
  },

  renderHTML({ node }): DOMOutputSpec {
    return [
      "div",
      {
        class: "docs-tab",
        role: "tabpanel",
        "data-type": "tab",
        "data-title": node.attrs.title,
      },
      0,
    ];
  },
});

export const CardGroup = Node.create({
  name: "cardGroup",
  group: "block",
  content: "card+",

  parseHTML() {
    return [{ tag: 'div[data-type="cardGroup"]' }];
  },

  renderHTML(): DOMOutputSpec {
    return ["div", { class: "docs-card-group", "data-type": "cardGroup" }, 0];
  },
});

export const Card = Node.create({
  name: "card",
  content: "block*",
  defining: true,

  addAttributes() {
    return {
      ...titleAttribute,
      href: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-href"),
        renderHTML: () => ({}),
      },
      icon: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-icon"),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="card"]', contentElement: ".docs-card-content" }];
  },

  renderHTML({ node }): DOMOutputSpec {
    const { title, icon } = node.attrs;
    const href = safeHref(node.attrs.href);
    const children: DOMOutputSpec[] = [];

    // Icons are an image URL or an emoji; icon-set names have no glyphs here.
    if (typeof icon === "string" && /^https:\/\//i.test(icon)) {
      children.push(["img", { class: "docs-card-icon", src: icon, alt: "" }]);
    } else if (typeof icon === "string" && /[^\x20-\x7e]/.test(icon)) {
      children.push(["span", { class: "docs-card-icon" }, icon]);
    }

    if (title) {
      children.push(
        href
          ? ["a", { class: "docs-card-title", href }, title]
          : ["div", { class: "docs-card-title" }, title],
      );
    }

    return [
      "div",
      {
        class: href ? "docs-card has-link" : "docs-card",
        "data-type": "card",
        "data-title": title,
        "data-href": href,
        "data-icon": icon,
      },
      ...children,
      ["div", { class: "docs-card-content" }, 0],
    ];
  },
});

export const DocsBlocks = [Steps, Step, Tabs, Tab, CardGroup, Card];

export default DocsBlocks;
